import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import SEO from '@/components/SEO';

const ContactPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  useEffect(() => {
    if (location.state?.itinerary) {
      const summary = location.state.itinerary.map(item =>
        `- ${item.name}: Date: ${item.date || 'TBD'}, Adults: ${item.adults}, Children (≤16): ${item.children}`
      ).join('\n');
      setFormData(prev => ({ ...prev, message: `I'm interested in:\n${summary}\n\nPlease contact me with a quote.` }));
    }
  }, [location.state]);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Inquiry Sent! We will contact you shortly.");
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Us"
        description="Get in touch with Peregrine Tours & Safaris. We are here to help you plan your perfect African adventure."
        keywords="Contact Safari Company, Travel Inquiry, Safari Quote, Victoria Falls Contact"
        canonicalUrl="https://www.peregrinetoursandsafaris.com/contact"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Peregrine Tours & Safaris",
          "description": "Get in touch with us for safari bookings and inquiries.",
          "mainEntity": {
            "@type": "Organization",
            "name": "Peregrine Tours & Safaris",
            "telephone": "+260 961 187 200",
            "email": "info@peregrinetoursandsafaris.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Plot# 2613/1747 Pula Street, Off Botswana Road",
              "addressLocality": "Livingstone",
              "addressCountry": "ZM"
            }
          }
        }}
      />

      <Navbar />
      <div className="pt-32 pb-20 container mx-auto px-4">
        <h1 className="text-4xl font-bold font-serif mb-8 text-center">Complete Your Inquiry</h1>
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12">
          <Card className="p-8 border-2">
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input placeholder="Full Name" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              <Input type="email" placeholder="Email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
              <Input placeholder="Phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              <Textarea rows={6} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} />
              <Button type="submit" className="w-full">Send Inquiry</Button>
            </form>
          </Card>
          <div className="space-y-8">
            <h3 className="text-2xl font-bold font-serif">Contact Details</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <MapPin className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <p className="font-bold">Address</p>
                  <p className="text-muted-foreground italic text-sm">Plot# 2613/1747 Pula Street, Off Botswana Road, Livingstone, Zambia</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Phone className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <p className="font-bold">Phone</p>
                  <a href="tel:+260961187200" className="text-muted-foreground hover:text-primary transition-colors">+260 961 187 200</a>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 text-primary flex-shrink-0 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold">WhatsApp</p>
                  <a href="https://wa.me/260961187200" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">Chat With Us</a>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Mail className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <p className="font-bold">Email</p>
                  <a href="mailto:info@peregrinetoursandsafaris.com" className="text-muted-foreground hover:text-primary transition-colors break-all">info@peregrinetoursandsafaris.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <Toaster />
    </div>
  );
};
export default ContactPage;